/**
 * Calendar entries that are not cases (Kelsi, Aug 20 2026, via Lloyd).
 *
 * Lloyd's appointments, court dates with no client attached, and staff time off. Backend is
 * `routes/CalendarEntry/CalendarEntryRouter.ts`.
 *
 * Every member of staff sees every entry — Kelsi settled that himself — but only the author can
 * change one. `canEdit` on each row is the backend's answer to that question, so the screen never
 * has to compare guids or hold anybody's identity to decide what to show.
 */

/** Mirrors `utils/calendar/calendarEntryKinds.ts`. Kept in step by hand; the values are in tests. */
export const CALENDAR_ENTRY_KINDS = ['appointment', 'court', 'time_off'] as const;

export type CalendarEntryKind = (typeof CALENDAR_ENTRY_KINDS)[number];

export const CALENDAR_ENTRY_KIND_LABELS: Record<CalendarEntryKind, string> = {
  appointment: 'Appointment',
  court: 'Court date, no case',
  time_off: 'Time off',
};

export interface CalendarEntryDto {
  CalendarEntry_guid: string;
  CalendarEntry_Title: string;
  CalendarEntry_Desc: string | null;
  /** Epoch milliseconds, the same unit as `CourtEvent_Start`. */
  CalendarEntry_Start: number;
  CalendarEntry_End: number;
  CalendarEntry_AllDay: boolean;
  CalendarEntry_Kind: CalendarEntryKind;
  CalendarEntry_CreatedByName: string | null;
  /** Whether the person looking at it may change it. Decided by the backend, not here. */
  canEdit: boolean;
}

export interface CalendarEntryInput {
  CalendarEntry_Title: string;
  CalendarEntry_Desc?: string | null;
  CalendarEntry_Start: number;
  CalendarEntry_End: number;
  CalendarEntry_AllDay?: boolean;
  CalendarEntry_Kind?: CalendarEntryKind;
}

declare const process: { env: { API_HOST?: string } };

export default class CalendarEntryApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  private static async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${CalendarEntryApi.host()}${path}`, {
      credentials: 'include',
      ...init,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Request failed');
    }
    // The delete answers 204 with no body.
    return response.status === 204 ? (undefined as T) : response.json();
  }

  static async list(range?: { from: number; to: number }): Promise<CalendarEntryDto[]> {
    const query = range ? `?from=${range.from}&to=${range.to}` : '';
    const body = await CalendarEntryApi.request<{ entries: CalendarEntryDto[] }>(
      `/api/v1/manager/calendar-entries${query}`,
    );
    return body.entries;
  }

  static async create(input: CalendarEntryInput): Promise<CalendarEntryDto> {
    return CalendarEntryApi.request<CalendarEntryDto>('/api/v1/manager/calendar-entries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  static async update(
    guid: string,
    patch: Partial<CalendarEntryInput>,
  ): Promise<CalendarEntryDto> {
    return CalendarEntryApi.request<CalendarEntryDto>(
      `/api/v1/manager/calendar-entries/${guid}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    );
  }

  static async remove(guid: string): Promise<void> {
    await CalendarEntryApi.request<void>(`/api/v1/manager/calendar-entries/${guid}`, {
      method: 'DELETE',
    });
  }
}
